import { useMutation } from 'react-query';
import { v14Response, Book } from '@readcloud/data';
import {
  MimeTypes,
  mimeTypeToExtension,
  mimeTypeConfigsByType,
} from '../types/mimeTypes';
import { tauriFetch } from '@readcloud/fetch-wrapper';
import { ABSOLUTE_PATH } from '@readcloud/state';
type ContentInput = {
  file?: File;
  url?: string;
  type: string;
};

function getImageMimeTypeAndExtension(base64String: string) {
  // This is a regex to extract the mime type and extension of an image file converted to a base64 string (kindly provided by our AI overlords)

  // Context:
  // Epubjs book covers are extracted from EpubBook's in base64 strings, which was the original purpose for this function.
  // We also use this function to get the mime type and extension for other uploaded images, by converting them to base64 before saving them to our form.

  const matches = base64String.match(
    /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/
  );

  if (!matches || matches.length !== 2) {
    throw new Error('Invalid base64 string format');
  }

  const mimeType = matches[1];
  const fileExtension = mimeType.split('/')[1].replace('jpeg', 'jpg');

  const allowedExtensions = mimeTypeConfigsByType['image'].map(
    (type) => type.extension
  );

  if (!allowedExtensions.some((ext) => ext === fileExtension)) {
    throw new Error(
      `File extension ${fileExtension} is not allowed. Allowed extensions are: ${allowedExtensions.join(
        ', '
      )}`
    );
  }

  return { mimeType, fileExtension };
}

export const useUploadMutations = (user: any) => {
  const uploadThumbnailMutation = useMutation({
    mutationFn: async (thumbnail: string) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      const { mimeType, fileExtension } =
        getImageMimeTypeAndExtension(thumbnail);

      const base64Response = await tauriFetch(thumbnail);
      const blob = await base64Response.blob();

      const formData = new FormData();
      formData.append('file', blob, `thumbnail.${fileExtension}`);

      const encodedMimeType = encodeURIComponent(mimeType);
      const url = `${ABSOLUTE_PATH}/api/rc/v14/upload/book?mimeType=${encodedMimeType}&fileExtension=${fileExtension}&rcsResourceType=Thumbnail`;

      const response = await tauriFetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });

      if (!response.ok) {
        console.log('Failed to upload thumbnail');
        throw new Error('Failed to upload thumbnail');
      }

      const data: v14Response<{ userUploadId: string }> = await response.json();
      return data.result;
    },
  });

  const uploadContentMutation = useMutation({
    mutationFn: async (content: ContentInput) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      if (content.url) {
        return;
      }

      if (content.file) {
        if (!mimeTypeToExtension[content.file.type as MimeTypes]) {
          throw new Error(`Unsupported content type: ${content.file.type}`);
        }

        const mimeType = content.file.type as MimeTypes;
        const fileExtension = mimeTypeToExtension[mimeType];
        const encodedMimeType = encodeURIComponent(mimeType);
        const baseUrl = `${ABSOLUTE_PATH}/api/rc/v14/upload/book?mimeType=${encodedMimeType}&fileExtension=${fileExtension}&rcsResourceType=Bookbytes`;

        const formData = new FormData();
        formData.append('file', content.file);

        const response = await tauriFetch(baseUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
          body: formData,
        });

        if (!response.ok) {
          console.log('Failed to upload content file');
          throw new Error('Failed to upload content file');
        }
        const data: v14Response<{ userUploadId: string }> =
          await response.json();
        return data.result;
      } else {
        console.log('A file must be provided to upload');
        throw new Error('A file must be provided to upload');
      }
    },
  });

  const submitContentMutation = useMutation({
    mutationFn: async (data: {
      clouds?: string[];
      title?: string;
      printable?: boolean;
      comments?: boolean;
      contentId?: string;
      thumbnailId?: string;
      content?: { type?: string; url?: string | null };
    }) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      const contentType =
        data.content?.type === 'website' || data.content?.type === 'document'
          ? 'Media'
          : 'EBook';

      const payload = {
        cloudIds: data.clouds,
        title: data.title,
        type: contentType,
        options: {
          printable: data.printable ?? false,
          comments: data.comments ?? false,
        },
        bookbytesUploadId: data.contentId,
        thumbnailUploadId: data.thumbnailId,
        downloadURI: data.content?.url,
      };

      const response = await tauriFetch(
        `${ABSOLUTE_PATH}/api/rc/v14/update/book/wc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        console.error('Failed to submit content data');
        throw new Error('Failed to submit content data');
      }

      return response.json();
    },
  });

  const updateContentMutation = useMutation({
    mutationFn: async (data: Book) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      const response = await tauriFetch(
        `${ABSOLUTE_PATH}/api/rc/v14/update/book/wc?bookId=${data.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        console.error('Failed to submit content data');
        throw new Error('Failed to submit content data');
      }

      return response.json();
    },
  });

  const updateBookDeletedMutation = useMutation({
    mutationFn: async ({ bookId, deleted }: { bookId: string; deleted: boolean }) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      const response = await tauriFetch(
        `${ABSOLUTE_PATH}/api/rc/v14/update/book/deleted?bookId=${bookId}&deleted=${deleted}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        console.error('Failed to update book deleted status');
        throw new Error('Failed to update book deleted status');
      }

      return response.json();
    },
  });

  const deleteContentMutation = useMutation({
    mutationFn: async (bookId: string) => {
      if (!user) {
        console.error('User is not logged in.');
        throw new Error('Not Authorized');
      }
      const response = await tauriFetch(
        `${ABSOLUTE_PATH}/api/rc/v14/update/book/wc?bookId=${bookId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({ deleted: true }),
        }
      );

      if (!response.ok) {
        console.error('Failed to delete content');
        throw new Error('Failed to delete content');
      }

      return response.json();
    },
  });

  return {
    uploadThumbnailMutation,
    uploadContentMutation,
    submitContentMutation,
    updateContentMutation,
    updateBookDeletedMutation,
    deleteContentMutation,
  };
};
